import type { ElementType, ReactNode } from 'react';
import { motion, type Variants } from 'framer-motion';

type AnimationType = 'blurInUp' | 'fadeIn' | 'slideUp';
type ByType = 'character' | 'word' | 'line';

const ANIMATION_VARIANTS: Record<AnimationType, Variants> = {
  blurInUp: {
    hidden: { opacity: 0, filter: 'blur(10px)', y: 20 },
    visible: { opacity: 1, filter: 'blur(0px)', y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  slideUp: {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
  },
};

const EASE = [0.25, 0.1, 0.25, 1] as const;

export interface TextAnimateProps {
  children: string;
  animation?: AnimationType;
  by?: ByType;
  stagger?: number;
  each?: number;
  delay?: number;
  className?: string;
  as?: ElementType;
}

function buildSegments(
  text: string,
  by: ByType,
  variants: Variants,
  itemTransition: object,
): ReactNode[] {
  if (by === 'character') {
    return text
      .trim()
      .split(' ')
      .map((word, wi, arr) => (
        <span key={wi} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {word.split('').map((char, ci) => (
            <motion.span
              key={ci}
              variants={variants}
              transition={itemTransition}
              style={{ display: 'inline-block' }}
            >
              {char}
            </motion.span>
          ))}
          {wi < arr.length - 1 && ' '}
        </span>
      ));
  }

  if (by === 'line') {
    return text.split('\n').map((line, i) => (
      <motion.span
        key={i}
        variants={variants}
        transition={itemTransition}
        style={{ display: 'block' }}
      >
        {line}
      </motion.span>
    ));
  }

  return text
    .trim()
    .split(' ')
    .map((word, i) => (
      <motion.span
        key={i}
        variants={variants}
        transition={itemTransition}
        style={{ display: 'inline-block' }}
      >
        {word}&nbsp;
      </motion.span>
    ));
}

export function TextAnimate({
  children,
  animation = 'blurInUp',
  by = 'word',
  stagger = 0.06,
  each = 0.4,
  delay = 0,
  className,
  as: Tag = 'div',
}: TextAnimateProps) {
  const variants = ANIMATION_VARIANTS[animation];
  const itemTransition = { duration: each, ease: EASE };
  const segments = buildSegments(children, by, variants, itemTransition);

  return (
    <Tag className={className}>
      <motion.span
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        transition={{ staggerChildren: stagger, delayChildren: delay }}
        style={{ display: by === 'line' ? 'block' : 'inline' }}
      >
        {segments}
      </motion.span>
    </Tag>
  );
}
